// @ts-check
import { inhalationAgentsArray } from '../inhalation-agents'
import { getDosePerKg } from '../utils/dose-per-kg'
import { cleanUpRound, toNumber } from '../utils/numbers'
import { replaceObjectRawKeys } from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { sortByNameIfExists } from '../utils/sort-by'
import { selectCalculateDrugWaste } from './case-props'
import {
  selectInfusionDrugTotals,
  selectSolutionFluidTotals,
} from './drug-totals'
import {
  selectFluidDosesAdministeredBySiteId,
  selectInfusionDosesAdministeredByDrugId,
  selectInhalationDosesAdministeredByDrugId,
  selectOralSedativeDosesAdministeredByDrugId,
  selectParenteralDosesAdministeredByDrugId,
} from './drugs-administered'
import { selectPatientWeightInKg } from './height-weight'
import { selectSelectedInfusionDrugs } from './infusion-drug'
import { selectIvSites } from './fluids'
import {
  selectLocalDosesAdministered,
  selectSelectedLocalsById,
} from './local-drug'
import {
  selectCaseOralSedatives,
  selectOralSedativeDosage,
} from './oral-sedative'
import { selectCaseParenDrugs, selectParenDrugDosage } from './paren-drug'
import { selectSedationLevel } from './sedation-level'
import { isNumber } from '../utils/is'
import { selectPreProcedureSedativeDosesAdministeredWithDrug } from './pre-proc-sedatives'

/** @type {CaseSelector<InfusionDrugReport[]>} */
export const selectInfusionDrugsReport = createSelector(
  selectSelectedInfusionDrugs,
  selectInfusionDosesAdministeredByDrugId,
  selectInfusionDrugTotals,
  /**
   * @param {SelectedInfusionDrug[]} drugs
   * @param {{ [drugId: string]: InfusionDoseAdministered[] }} administeredByDrugId
   * @param {SingleCaseParenDrugTotalsById} drugTotals
   */
  (drugs, administeredByDrugId, drugTotals) => {
    /** @type {InfusionDrugReport[]} */
    const toReturn = []
    for (const drug of drugs) {
      const { name, id, unit, bolusUnit, pumpKg } = drug
      // NB: we will only have "totals" for confirmed values
      const totals = drugTotals[id]
      const administered = administeredByDrugId[id]
      const boluses = administered
        ? administered.filter(d => d.route === 'infusionBolus')
        : []
      const infusions = administered
        ? administered.filter(d => d.route === 'infusion')
        : []

      /** @type {InfusionDrugReport} */
      const entry = {
        id,
        name,
        total: isNumber(totals && totals.total) ? totals.total : null,
        wasted: isNumber(totals && totals.wasted) ? totals.wasted : null,
        pumpKg,
        unit,
        bolusUnit,
        boluses,
        infusions,
        // @ts-ignore
        totalUnit: (totals && totals.unit) || null,
      }

      toReturn.push(entry)
    }
    return toReturn
  }
)

/**
 * This one simply filters out drugs that have no infusions or boluses. This is
 * for use on the final record, where as the above can be used to render the
 * infusions graph preview.
 *
 * @type {CaseSelector<InfusionDrugReport[]>}
 */
export const selectInfusionDrugsFinalReport = createSelector(
  selectInfusionDrugsReport,
  /**
   * @param {InfusionDrugReport[]} drugs
   * @returns {InfusionDrugReport[]}
   */
  drugs => drugs.filter(drug => drug.infusions.length || drug.boluses.length)
)

/** @type {CaseSelector<FluidReport[]>} */
export const selectFluidsReport = createSelector(
  selectIvSites,
  selectFluidDosesAdministeredBySiteId,
  selectSolutionFluidTotals,
  /**
   * @param {IvSite[]} sites
   * @param {any} dosesBySiteId
   * @param {FluidTotals} fluidTotals
   * @returns {FluidReport[]}
   */
  (sites, dosesBySiteId, fluidTotals) =>
    sites.map(site => {
      const id = site.id
      /** @type {IvSolution[]} */
      const solutions = site.solutions

      const doses = dosesBySiteId[id] || []
      const flowRateDoses = doses.filter(d => d.route === 'fluid')
      const bolusDoses = doses.filter(d => d.route === 'fluidBolus')
      const syringeFlushes = doses.filter(d => d.route === 'syringeFlush')
      const lastDose = flowRateDoses[flowRateDoses.length - 1] || null
      const lastBolus = bolusDoses[bolusDoses.length - 1] || null
      const hasNonZeroDose = doses.some(dose => dose.amount)
      const totals = fluidTotals.bySite[id] || []

      const solutionsWithDoseData = solutions.map(sol => {
        const filter = d => d.bagId === sol.id
        const solDoses = doses.filter(filter)
        const solFlowRateDoses = flowRateDoses.filter(filter)
        const solBolusDoses = bolusDoses.filter(filter)
        const lastDose = solFlowRateDoses[solFlowRateDoses.length - 1] || null
        const lastBolus = solBolusDoses[solBolusDoses.length - 1] || null
        const solutionHasNonZeroDose = solDoses.some(dose => dose.amount)
        return {
          ...sol,
          doses: solDoses,
          flowRateDoses: solFlowRateDoses,
          bolusDoses: solBolusDoses,
          lastDose,
          lastBolus,
          hasNonZeroDose: solutionHasNonZeroDose,
        }
      })
      return {
        ...site,
        hasNonZeroDose,
        doses,
        syringeFlushes,
        flowRateDoses,
        bolusDoses,
        lastDose,
        lastBolus,
        solutions: solutionsWithDoseData,
        totals,
      }
    })
)

/** @type {CaseSelector<FluidReport[]>} */
export const selectFluidsFinalReport = createSelector(
  selectFluidsReport,
  /** @param {FluidReport[]} fluids */
  fluids => fluids.filter(x => x.side && x.name)
)

/**
 * @remarks
 *   This selector is used to generate the a report of all parenteral drugs based
 *   on drugs selected for the case. It's includes both pre-procedure and during
 *   case doses.
 *
 *   Inclusion is based on whether there is a "dosage" for the drug. Which is
 *   analogous to it having been selected for a case.
 * @type {CaseSelector<ParenDrugReport[]>}
 */
export const selectParenDrugsReport = createSelector(
  selectCaseParenDrugs,
  selectParenteralDosesAdministeredByDrugId,
  selectParenDrugDosage,
  selectCalculateDrugWaste,
  selectPatientWeightInKg,
  /**
   * @param {CaseData['parenDrugs']} caseParenDrugs
   * @param {{ [drugId: string]: ParenDoseAdministered[] }} dosesAdministeredById
   * @param {{ [drugId: string]: ParenDrugDosageProcessed }} dosageProcessedById
   * @param {boolean} calculateDrugWaste
   * @param {number | null} patientWeightInKg
   * @returns {ParenDrugReport[]}
   */
  (
    caseParenDrugs,
    dosesAdministeredById,
    dosageProcessedById,
    calculateDrugWaste,
    patientWeightInKg
  ) => {
    /** @type {{ [drugId: string]: ParenDrugReport }} */
    const result = {}
    for (const drugId in dosageProcessedById) {
      /** We grab unit here, because it will be consistent for all of these. */
      const { unit, name } = caseParenDrugs[drugId] || {}
      const dosesRaw = dosesAdministeredById[drugId] || []
      /** @type {Omit<ParenDrugReportDose, 'pctOfMax'>[]} */
      const doses = []
      let maxDose = 0
      let total = 0
      let givenDuringProcedure = 0
      for (const dose of dosesRaw) {
        const {
          amount,
          amount_raw,
          time,
          route,
          id,
          isPreProcedure,
        } = replaceObjectRawKeys(dose)
        const perKg = getDosePerKg(amount, unit, patientWeightInKg)
        /** @type {Omit<ParenDrugReportDose, 'pctOfMax'>} */
        const reportDose = {
          id,
          amount,
          amount_raw,
          unit,
          time,
          route,
          isPreProcedure,
        }
        if (perKg) {
          reportDose.perKg = perKg
        }
        // track max
        maxDose = Math.max(maxDose, reportDose.amount)

        // track total
        total += reportDose.amount
        if (!isPreProcedure) {
          givenDuringProcedure += reportDose.amount
        }

        doses.push(reportDose)
      }

      const dosage = dosageProcessedById[drugId]
      let wasted = dosage.wasted
      total = cleanUpRound(total)
      if (calculateDrugWaste) {
        wasted = cleanUpRound((dosage.totalDrawn || 0) - total)
      }

      result[drugId] = {
        id: drugId,
        name,
        unit,
        total,
        givenDuringProcedure: cleanUpRound(givenDuringProcedure),
        maxDose,
        wasted: toNumber(wasted),
        doses: doses.map(dose => ({
          ...dose,
          pctOfMax: cleanUpRound(maxDose ? dose.amount / maxDose : 0),
        })),
      }
    }

    return Object.values(result).sort(sortByNameIfExists)
  }
)

/**
 * This one simply filters out drugs that have no recorded doses or waste.
 *
 * @type {CaseSelector<ParenDrugReport[]>}
 */
export const selectParenDrugsFinalReport = createSelector(
  selectParenDrugsReport,
  /**
   * @param {ParenDrugReport[]} drugs
   * @returns {ParenDrugReport[]}
   */
  drugs => drugs.filter(drug => drug.total || drug.wasted)
)

/**
 * @remarks
 *   This selector is used to generate the a report of all oral sedatives based on
 *   on drugs selected for the case.
 *
 *   Inclusion is based on whether there is a "dosage" for the drug. Which is
 *   analogous to it having been selected for a case.
 * @type {CaseSelector<OralSedativeReport[]>}
 */
export const selectOralSedativesReport = createSelector(
  selectCaseOralSedatives,
  selectOralSedativeDosesAdministeredByDrugId,
  selectOralSedativeDosage,
  selectPatientWeightInKg,
  /**
   * @param {CaseData['oralSedatives']} caseOralSedatives
   * @param {{ [drugId: string]: OralDoseAdministered[] }} dosesAdministeredById
   * @param {{ [drugId: string]: OralDrugDosageProcessed }} dosageProcessedById
   * @param {number | null} patientWeightInKg
   * @returns {OralSedativeReport[]}
   */
  (
    caseOralSedatives,
    dosesAdministeredById,
    dosageProcessedById,
    patientWeightInKg
  ) => {
    /** @type {{ [drugId: string]: OralSedativeReport }} */
    const result = {}
    for (const drugId in dosageProcessedById) {
      const { unit, name } = caseOralSedatives[drugId] || {}
      const dosesRaw = dosesAdministeredById[drugId] || []
      /** @type {Omit<OralSedativeReportDose, 'pctOfMax'>[]} */
      const doses = []
      let maxDose = 0
      let total = 0
      let givenDuringProcedure = 0
      for (const dose of dosesRaw) {
        const {
          amount,
          time,
          route,
          isPreProcedure,
          isComplete,
          id,
        } = replaceObjectRawKeys(dose)
        const perKg = getDosePerKg(amount, unit, patientWeightInKg)
        /** @type {Omit<OralSedativeReportDose, 'pctOfMax'>} */
        const reportDose = {
          id,
          amount,
          unit,
          time,
          route,
          isPreProcedure,
          isComplete,
        }
        if (perKg) {
          reportDose.perKg = perKg
        }

        // only include complete doses
        // in totals
        if (isComplete) {
          // track max
          maxDose = Math.max(maxDose, reportDose.amount)

          // track total
          total += reportDose.amount
          if (!isPreProcedure) {
            givenDuringProcedure += reportDose.amount
          }
        }

        doses.push(reportDose)
      }

      result[drugId] = {
        id: drugId,
        name,
        unit,
        total: cleanUpRound(total),
        maxDose,
        givenDuringProcedure: cleanUpRound(givenDuringProcedure),
        doses: doses.map(dose => ({
          ...dose,
          pctOfMax:
            maxDose && dose.isComplete
              ? cleanUpRound(dose.amount / maxDose)
              : 0,
        })),
      }
    }

    return Object.values(result).sort(sortByNameIfExists)
  }
)

/** @type {CaseSelector<boolean>} */
export const selectHasGivenOralSedativesDuringProcedure = createSelector(
  selectOralSedativesReport,
  /** @param {OralSedativeReport[]} drug */
  drug => drug.some(drug => drug.givenDuringProcedure > 0)
)

/**
 * This one filters out doses that are being edited or are zero amount and
 * recomputes the total.
 *
 * @type {CaseSelector<OralSedativeReport[]>}
 */
export const selectOralSedativesFinalReport = createSelector(
  selectOralSedativesReport,
  /**
   * @param {OralSedativeReport[]} drugs
   * @returns {OralSedativeReport[]}
   */
  drugs =>
    drugs
      .map(drug => {
        const { doses, ...rest } = drug
        let totalOfCompleteDoses = 0
        let maxDose = 0
        let givenDuringProcedure = 0
        const completeDoses = doses.filter(dose => dose.isComplete)

        /** @type {OralSedativeReportDose[]} */
        const dosesToReturn = []

        for (const dose of completeDoses) {
          totalOfCompleteDoses += dose.amount
          maxDose = Math.max(maxDose, dose.amount)
          if (!dose.isPreProcedure) {
            givenDuringProcedure += dose.amount
          }
        }

        for (const dose of completeDoses) {
          dosesToReturn.push({
            ...dose,
            pctOfMax: cleanUpRound(maxDose ? dose.amount / maxDose : 0),
          })
        }

        return {
          ...rest,
          total: cleanUpRound(totalOfCompleteDoses),
          maxDose,
          givenDuringProcedure: cleanUpRound(givenDuringProcedure),
          doses: dosesToReturn,
        }
      })
      .filter(drug => drug.total)
)

/** @type {CaseSelector<InhalationReport[]>} */
export const selectInhalationReport = createSelector(
  selectSedationLevel,
  selectInhalationDosesAdministeredByDrugId,
  /**
   * @param {SedationLevelCased} sedationLevel
   * @param {{ [drugId: string]: InhalationDoseAdministered[] }} dosesByDrugId
   * @returns {InhalationReport[]}
   */
  (sedationLevel, dosesByDrugId) => {
    const isDeepOrGA = sedationLevel === 'Deep' || sedationLevel === 'GA'
    const filtered = isDeepOrGA
      ? inhalationAgentsArray
      : inhalationAgentsArray.filter(agent => !agent.isAA && !agent.deepGaOnly)

    return filtered.map(({ id, name, color, unit, step, max, isAA }) => {
      const records = (dosesByDrugId[id] || []).map(({ id, amount, time }) => ({
        id,
        amount,
        time,
      }))
      const hasBeenUsed =
        !!records.length && records.some(record => record.amount > 0)
      const lastRecord = records[records.length - 1]
      const currentDose = (lastRecord && lastRecord.amount) || 0

      return {
        id,
        name,
        step,
        max,
        color,
        unit,
        isAA: !!isAA,
        currentDose,
        hasBeenUsed,
        records,
      }
    })
  }
)

/**
 * Only includes the ones that were actually used.
 *
 * @type {CaseSelector<InhalationReport[]>}
 */
export const selectInhalationFinalReport = createSelector(
  selectInhalationReport,
  /**
   * @param {InhalationReport[]} drugs
   * @returns {InhalationReport[]}
   */
  drugs => drugs.filter(drug => drug.hasBeenUsed)
)

/** @type {CaseSelector<boolean>} */
export const selectHasGivenInhalationAgents = createSelector(
  selectInhalationFinalReport,
  drugs => drugs.length > 0
)

/** @type {CaseSelector<LocalDoseReportEntry[]>} */
export const selectLocalAnestheticsFinalReport = createSelector(
  selectLocalDosesAdministered,
  selectSelectedLocalsById,
  (doses, variantById) =>
    doses.reduce((result, { id, drugVariantId, time, mg, epiMg }) => {
      const drugVariant = variantById[drugVariantId]
      const drugName =
        drugVariant && (drugVariant.nameWithPct || drugVariant.name)
      if (drugName) {
        /** @type {LocalDoseReportEntry} */
        const entry = {
          id,
          drugName,
          time,
          mg,
          epiMg,
        }
        result.push(entry)
      }
      return result
    }, [])
)

/** @type {CaseSelector<PreProcedureSedativeReport[]>} */
export const selectPreProcedureSedativeReport = createSelector(
  selectPreProcedureSedativeDosesAdministeredWithDrug,
  /** @param {PreProcedureSedativeDoseWithDrug[]} preProcedureDoses */
  preProcedureDoses => {
    /** @type {PreProcedureSedativeReport[]} */
    const result = []
    for (const dose of preProcedureDoses) {
      const { id, isComplete, time, amount, route, drug } = dose
      // we know "drug" is there because we explicitly filter out
      // ones that don't have it in the input selector
      const { name, unit } = drug
      if (isComplete) {
        result.push({
          id,
          name,
          amount,
          route,
          unit,
          time,
        })
      }
    }
    return result
  }
)
